import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import api from "../../utils/apiAuth";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import tr from "date-fns/locale/tr";
import {
  customSelectStyles,
  customSelectComponents,
} from "../../utils/InputStyles";

registerLocale("tr", tr);

const PersonalModal = React.memo(({ onDateChange }) => {
  const { user } = useSelector((state) => state.user);
  const [positionOptions, setPositionOptions] = useState([]);
  console.log(user, "user");

  // Doğum tarihini Date objesine çevir
  const parseDate = useCallback((dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date;
  }, []);

  const initialPersonalData = useMemo(
    () => ({
      name: user?.data?.name || "",
      country: "",
      city: "",
      birthDate: parseDate(user?.data?.birthDate) || null,
      gender: user?.data?.gender || "",
      position: user?.data?.position?.name || "",
    }),
    [
      user?.data?.name,
      user?.data?.birthDate,
      user?.data?.gender,
      user?.data?.position?.name,
      parseDate,
    ]
  );

  const [personalData, setPersonalData] = useState(initialPersonalData);
  const [countryOptions, setCountryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  // İlk yüklemede doğum tarihini gönder
  useEffect(() => {
    if (personalData.birthDate) {
      onDateChange(personalData.birthDate);
    }
  }, []);

  // Cinsiyet seçenekleri
  const genderOptions = useMemo(
    () => [
      { value: "", label: "Seçiniz" },
      { value: "MALE", label: "Erkek" },
      { value: "FEMALE", label: "Kadın" },
      { value: "OTHER", label: "Diğer" },
    ],
    []
  );

  const fetchCountries = useCallback(async () => {
    try {
      const response = await api.get("/locations/countries");
      const countries = response.data.data || [];
      const formattedCountries = countries.map((country) => ({
        value: country.isoCode,
        label: country.name,
      }));
      setCountryOptions(formattedCountries);
    } catch (error) {
      console.error("Ülkeler çekilirken hata oluştu:", error);
    }
  }, []);

  const fetchCities = useCallback(async () => {
    if (!personalData.country) {
      setCityOptions([]);
      return;
    }
    try {
      const response = await api.get(
        `/locations/countries/${personalData.country}/cities`
      );
      const cities = response.data.data || [];
      const formattedCities = cities.map((city) => ({
        value: city.name,
        label: city.name,
      }));
      setCityOptions(formattedCities);
    } catch (error) {
      console.error("Şehirler çekilirken hata oluştu:", error);
    }
  }, [personalData.country]);

  const fetchPositions = useCallback(async () => {
    try {
      const response = await api.get("/positions/positionNames");
      const positions = response.data.data || [];
      const formattedPositions = positions.map((position) => ({
        value: position.name,
        label: position.name,
      }));
      setPositionOptions(formattedPositions);
    } catch (error) {
      console.error("Pozisyonlar çekilirken hata oluştu:", error);
    }
  }, []);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  useEffect(() => {
    try {
      let country = "";
      let city = "";
      if (user?.data?.address) {
        const [addrCountry, addrCity] = user.data.address.split(" - ");
        country = addrCountry || "";
        city = addrCity || "";
      }
      setPersonalData((prev) => ({
        ...prev,
        name: user?.data?.name || "",
        country: country,
        city: city,
        birthDate: parseDate(user?.data?.birthDate) || null,
        gender: user?.data?.gender || "",
        position: user?.data?.position?.name || "",
      }));
    } catch (e) {
      console.error("Kullanıcı verileri set edilirken hata:", e);
    }
  }, [user, parseDate]);

  const handleNameChange = useCallback((e) => {
    setPersonalData((prev) => ({ ...prev, name: e.target.value }));
  }, []);

  const handleCountryChange = useCallback((selectedOption) => {
    setPersonalData((prev) => ({
      ...prev,
      country: selectedOption ? selectedOption.value : "",
      city: "",
    }));
  }, []);

  const handleCityChange = useCallback((selectedOption) => {
    setPersonalData((prev) => ({
      ...prev,
      city: selectedOption ? selectedOption.value : "",
    }));
  }, []);

  const handleBirthDateChange = useCallback(
    (date) => {
      setPersonalData((prev) => ({ ...prev, birthDate: date }));
      onDateChange(date);
    },
    [onDateChange]
  );

  const handleGenderChange = useCallback((selectedOption) => {
    setPersonalData((prev) => ({
      ...prev,
      gender: selectedOption ? selectedOption.value : "",
    }));
  }, []);

  const handlePositionChange = useCallback((selectedOption) => {
    setPersonalData((prev) => ({
      ...prev,
      position: selectedOption ? selectedOption.value : "",
    }));
  }, []);

  const selectedCountry = useMemo(
    () =>
      countryOptions.find((option) => option.value === personalData.country) ||
      null,
    [countryOptions, personalData.country]
  );

  const selectedCity = useMemo(
    () =>
      cityOptions.find((option) => option.value === personalData.city) || null,
    [cityOptions, personalData.city]
  );

  const selectedGender = useMemo(
    () =>
      genderOptions.find((option) => option.value === personalData.gender) ||
      null,
    [genderOptions, personalData.gender]
  );

  const selectedPosition = useMemo(
    () =>
      positionOptions.find(
        (option) => option.value === personalData.position
      ) || null,
    [positionOptions, personalData.position]
  );

  const hasActiveProject =
    user?.data?.projects && user.data.projects.length > 0;

  return (
    <div>
      <div className="form-row mt-2 flex flex-col sm:flex-row gap-4">
        <div className="form-group w-full sm:w-1/2">
          <label htmlFor="name" className="label-edit">
            İsim ve Soyisim
          </label>
          <input
            className="custom-input w-full px-4"
            placeholder="Ad Soyad"
            id="name"
            name="name"
            type="text"
            value={personalData.name}
            onChange={handleNameChange}
          />
        </div>
        <div className="form-group w-full sm:w-1/2">
          <label htmlFor="position" className="label-edit">
            Aktif Rol
          </label>
          <Select
            options={positionOptions}
            value={selectedPosition}
            onChange={handlePositionChange}
            placeholder="Rol seçiniz"
            styles={customSelectStyles}
            components={customSelectComponents}
            isClearable
            isDisabled={hasActiveProject}
            className="w-full"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          {hasActiveProject && (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                <strong>Bilgilendirme:</strong> Aktif bir projede yer aldığınız
                için rolünüzü değiştiremezsiniz. Rol değişikliği yapmak için
                önce projeden ayrılmanız gerekmektedir.
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="form-row mt-4 flex flex-col sm:flex-row gap-4">
        <div className="form-group w-full sm:w-1/2">
          <label htmlFor="country" className="label-edit">
            Ülke
          </label>
          <Select
            options={countryOptions}
            value={selectedCountry}
            onChange={handleCountryChange}
            placeholder="Ülke seçiniz"
            styles={customSelectStyles}
            components={customSelectComponents}
            isClearable
            className="w-full"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
        <div className="form-group w-full sm:w-1/2">
          <label htmlFor="city" className="label-edit">
            Şehir
          </label>
          <Select
            options={cityOptions}
            value={selectedCity}
            onChange={handleCityChange}
            placeholder="Şehir seçiniz"
            styles={customSelectStyles}
            components={customSelectComponents}
            isClearable
            className="w-full"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      </div>
      <div className="form-row mt-4 flex flex-col sm:flex-row gap-4">
        <div className="form-group w-full sm:w-1/2">
          <label className="label-edit">Doğum Tarihi</label>
          <div className="w-full">
            <DatePicker
              selected={personalData.birthDate}
              onChange={handleBirthDateChange}
              locale="tr"
              dateFormat="dd/MM/yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="scroll"
              placeholderText="Tarih seçiniz"
              className="custom-input w-full px-4"
              maxDate={new Date()}
              yearDropdownItemNumber={100}
              scrollableYearDropdown
              wrapperClassName="w-full"
              popperModifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, 8],
                  },
                },
              ]}
            />
          </div>
        </div>
        <div className="form-group w-full sm:w-1/2">
          <label className="label-edit">Cinsiyet</label>
          <Select
            options={genderOptions}
            value={selectedGender}
            onChange={handleGenderChange}
            placeholder="Seçiniz"
            styles={customSelectStyles}
            components={customSelectComponents}
            className="w-full"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      </div>
      <input type="hidden" name="gender" value={personalData.gender} />
      <input type="hidden" name="country" value={personalData.country} />
      <input type="hidden" name="city" value={personalData.city} />
      <input type="hidden" name="position" value={personalData.position} />
    </div>
  );
});

export default PersonalModal;
